import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase.ts";
import { allSessions, mutateSession, type LocalSession } from "./queue.ts";
import { drain } from "./sync.ts";
import { waitingRows } from "./prune.ts";

// The sync loop for the whole app, not for one screen.
//
// It used to live inside the tracker and drained only the session on screen, so
// anything still queued when a session ended without signal, or when the page
// reloaded, was never sent: nothing else ever called drain again. Code review of
// PR #3 found it from three separate angles.
//
// Runs on sign-in, when the browser reports it is back online, when the tab
// becomes visible again (a phone returning from the lock screen), and every 30
// seconds. drain is single flight per session, so these overlapping triggers
// cost nothing extra.

export interface SyncOverview {
  /** Rows on this phone not yet on the server, across this person's sessions. */
  waiting: number;
  /** Sessions ended on this phone whose end the server has not been told about. */
  endsWaiting: number;
  /** This person's sessions that were never ended, except the one on screen. */
  unfinished: LocalSession[];
  /** When the queue was read for these numbers. A screen that must not claim
      "uploaded" on stale numbers compares this with its own moment. */
  checkedAt: number;
  /** The phone's storage could not be read, so the numbers above say nothing.
      A failed read used to publish zeros, which read as "everything uploaded". */
  unreadable: boolean;
}

const EMPTY: SyncOverview = { waiting: 0, endsWaiting: 0, unfinished: [], checkedAt: 0, unreadable: false };

// Records written before 2026-09-17 carry no owner, and they used to count as
// everyone's: a second person signing in on the same phone was offered the
// first person's session to resume. The server knows whose each session is,
// and only shows a person their own unless they are an admin, so ask it once
// and write the answer onto the record.
//
// A session the server does not return (deleted there, or someone else's) used
// to be asked about again every 30 seconds for as long as the phone kept it
// (EFF-1). An answer of nothing is remembered for a day, for the person who
// asked, before asking again.
const ASK_AGAIN_MS = 24 * 60 * 60 * 1000;

async function adoptOwners(sessions: LocalSession[], userId: string) {
  const now = Date.now();
  const askedRecently = (s: LocalSession) =>
    s.ownerChecked?.by === userId && now - s.ownerChecked.at <= ASK_AGAIN_MS;
  const unowned = sessions.filter((s) => !s.observerId && !askedRecently(s)).map((s) => s.id);
  if (unowned.length === 0) return;
  const { data, error } = await supabase
    .from("observation_sessions").select("id,observer_id").in("id", unowned);
  if (error || !data) return;
  const answered = new Set<string>();
  for (const row of data) {
    answered.add(row.id as string);
    await mutateSession(row.id, null, (s) => ({ ...s, observerId: row.observer_id as string }));
  }
  for (const id of unowned) {
    if (!answered.has(id)) await mutateSession(id, null, (s) => ({ ...s, ownerChecked: { by: userId, at: now } }));
  }
}

export function useBackgroundSync(userId: string | null, onScreenSessionId: string | null): [SyncOverview, () => void] {
  const [overview, setOverview] = useState<SyncOverview>(EMPTY);

  // Who is signed in now. A run that began under someone else stops at its next
  // step, rather than sending or showing that person's sessions under this
  // sign-in. Declared before the loop's effect so it updates first.
  const current = useRef(userId);
  useEffect(() => {
    current.current = userId;
    setOverview(EMPTY);
  }, [userId]);

  const run = useCallback(async () => {
    if (!userId) return;
    const stillMe = () => current.current === userId;
    const mine = async () => (await allSessions()).filter((s) => s.observerId === userId);

    try {
      if (navigator.onLine) {
        await adoptOwners(await allSessions(), userId);
        for (const s of await mine()) {
          if (!stillMe()) return;
          const needsEnd = s.endedAt !== null && !s.endSyncedAt;
          if (waitingRows(s) > 0 || needsEnd) await drain(s.id, userId).catch(() => undefined);
        }
      }
    } catch {
      // Storage or network trouble. The read below decides what is shown.
    }

    if (!stillMe()) return;
    const checkedAt = Date.now();
    let sessions: LocalSession[];
    try {
      sessions = await mine();
    } catch {
      if (stillMe()) setOverview((o) => ({ ...o, checkedAt, unreadable: true }));
      return;
    }
    if (!stillMe()) return;
    setOverview({
      waiting: sessions.reduce((n, s) => n + waitingRows(s), 0),
      endsWaiting: sessions.filter((s) => s.endedAt !== null && !s.endSyncedAt).length,
      unfinished: sessions.filter((s) => s.endedAt === null && s.id !== onScreenSessionId),
      checkedAt,
      unreadable: false,
    });
  }, [userId, onScreenSessionId]);

  useEffect(() => {
    if (!userId) return;
    const kick = () => { void run(); };
    const onVisible = () => { if (document.visibilityState === "visible") kick(); };
    kick();
    const timer = setInterval(kick, 30000);
    addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, run]);

  return [overview, () => { void run(); }];
}
